import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import RocketLaunchOutlined from "@mui/icons-material/RocketLaunchOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import { ACCOUNTS } from "@/data/mock-data";
import type { MarketplaceTemplate } from "@/data/mock-data";
import { formatUSD, formatNumericInput } from "@/lib/formatters";
import { TRADE_TIMING_OPTIONS, DEFAULT_TRADE_TIMING } from "@/lib/trade-timing";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";
import { BotSettingsForm } from "@/components/BotSettingsForm";
import { ConnectBrokerPanel } from "@/components/ConnectBrokerPanel";
import type { ConnectedBrokerAccount } from "@/components/ConnectBrokerPanel";

// ---- Types ----------------------------------------------------------------

interface BotAcquireFlowProps {
  open: boolean;
  /** The Marketplace template being acquired. */
  template: MarketplaceTemplate | null;
  onClose: () => void;
  /** Called once the bot is launched — message surfaced as a toast. */
  onDone: (message: string) => void;
}

type Step = "amount" | "settings" | "run" | "connect" | "balance";

const BUY_QUICK_ADDS = [100, 1000, 10000] as const;

/** Sentinel Select values that open the broker-connect sub-flow. */
const ADD_BROKER = "__add_broker__";
const NEW_PAPER = "__new_paper__";

/**
 * MenuProps for the run-step selects. The dropdown must paint ABOVE the
 * flow drawer (which sits at `modal + 2`) — otherwise the sheet
 * occludes the open menu (Sergey 2026-05-21). Height-capped so a long
 * account list scrolls rather than overflowing the viewport.
 */
const SELECT_MENU_PROPS = {
  sx: (th: Theme) => ({ zIndex: th.zIndex.modal + 10 }),
  PaperProps: { sx: { maxHeight: 320 } }
};

// ---- Component ------------------------------------------------------------

/**
 * Marketplace Buy flow (Sergey 2026-05-21). A multi-step bottom sheet:
 *
 *   amount   → how much to invest (quick-add chips + free entry).
 *   settings → wizard-bot params form. Skipped for rebalancing
 *              templates (`botType !== "wizard"`).
 *   run      → assign a broker account + choose when to start, then
 *              launch. The account picker can branch into…
 *   connect  → the Connect Broker panel (grid + WhiteHat Paper create).
 *
 * Acquiring is free — see project memory on tiered (not per-bot)
 * subscriptions. There's no live order router, so the launch is
 * acknowledged optimistically via `onDone`.
 */
export function BotAcquireFlow({
  open,
  template,
  onClose,
  onDone
}: BotAcquireFlowProps) {
  // Hold the last template through the slide-out animation.
  const [snapshot, setSnapshot] = useState<MarketplaceTemplate | null>(
    template
  );
  if (template && template !== snapshot) setSnapshot(template);

  const [step, setStep] = useState<Step>("amount");
  const [amountStr, setAmountStr] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [createdAccounts, setCreatedAccounts] = useState<
    ConnectedBrokerAccount[]
  >([]);
  const [startWhen, setStartWhen] = useState<string>(DEFAULT_TRADE_TIMING);
  const [connectView, setConnectView] = useState<"grid" | "paper">("grid");

  // Fresh state every time the flow opens.
  useEffect(() => {
    if (open) {
      setStep("amount");
      setAmountStr("");
      setSelectedAccountId("");
      setCreatedAccounts([]);
      setStartWhen(DEFAULT_TRADE_TIMING);
      setConnectView("grid");
    }
  }, [open, template?.id]);

  const amount = useMemo(() => {
    const n = parseFloat(amountStr);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [amountStr]);

  const t = snapshot;
  if (!t) return null;

  const isWizard = t.botType === "wizard";

  // Account options: paper accounts created in this flow first, then
  // the user's existing accounts.
  const accountOptions = [
    ...createdAccounts.map((a) => ({
      id: a.id,
      label: a.name,
      sub: a.paper ? "WhiteHatBots Paper" : a.provider
    })),
    ...ACCOUNTS.map((a) => ({
      id: a.id,
      label: a.name,
      sub: `${a.provider} ${a.masked}${a.paper ? " · Paper" : ""}`
    }))
  ];
  const selectedAccount = accountOptions.find(
    (a) => a.id === selectedAccountId
  );
  const selectedRealAccount = ACCOUNTS.find(
    (a) => a.id === selectedAccountId
  );
  const selectedCreated = createdAccounts.find(
    (a) => a.id === selectedAccountId
  );
  // Uniform view of the selected account, whether it's an existing one
  // (carries real balances) or one just connected in this flow. A
  // freshly connected account is treated as unfunded — $0 buying power
  // — until the user adds money to it.
  const selectedInfo = selectedRealAccount
    ? {
        name: selectedRealAccount.name,
        isLive: !selectedRealAccount.paper,
        buyingPower: selectedRealAccount.buyingPower,
        cash: selectedRealAccount.cashValue,
        isNew: false
      }
    : selectedCreated
      ? {
          name: selectedCreated.name,
          isLive: !selectedCreated.paper,
          buyingPower: 0,
          cash: 0,
          isNew: true
        }
      : null;
  // Drives the "real capital" warning vs. the paper "simulated funds" note.
  const selectedIsLive = !!selectedInfo && selectedInfo.isLive;
  // Live account picked but the invest amount exceeds its buying power
  // (covers both existing accounts and freshly connected unfunded ones).
  const insufficientFunds =
    !!selectedInfo &&
    selectedInfo.isLive &&
    amount > selectedInfo.buyingPower;

  function bumpAmount(delta: number) {
    const next = (parseFloat(amountStr) || 0) + delta;
    setAmountStr(String(Math.round(next * 100) / 100));
  }

  function handleAccountChange(v: string) {
    if (v === ADD_BROKER) {
      setConnectView("grid");
      setStep("connect");
    } else if (v === NEW_PAPER) {
      setConnectView("paper");
      setStep("connect");
    } else {
      setSelectedAccountId(v);
    }
  }

  function handleBrokerCreated(acct: ConnectedBrokerAccount) {
    setCreatedAccounts((prev) => [acct, ...prev]);
    setSelectedAccountId(acct.id);
    // A freshly connected LIVE account is unfunded — route through the
    // balance step so the user sees they need to add money before the
    // bot can run. Paper accounts trade on simulated funds, so they go
    // straight to Run. Sergey 2026-05-21.
    setStep(acct.paper ? "run" : "balance");
  }

  function handleLaunch() {
    onDone(
      `Launched ${t!.name} on ${selectedAccount?.label ?? "your account"}` +
        `${amount > 0 ? ` with ${formatUSD(amount)}` : ""} — starting ` +
        `${startWhen.toLowerCase()}.`
    );
    onClose();
  }

  // Step title for the header.
  const title =
    step === "amount"
      ? `Buy ${t.name}`
      : step === "settings"
        ? "Bot settings"
        : step === "connect"
          ? "Connect broker"
          : step === "balance"
            ? "Account balance"
            : `Run "${t.name}"`;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          // FIXED height (not max-height): each step has a different
          // content height (short amount step vs. tall settings form),
          // and a content-driven height made the drawer jump up/down
          // between steps. Pinning to 92vh keeps the top edge still —
          // same treatment as BotDetailDrawer. Sergey 2026-05-21.
          height: "92vh",
          display: "flex",
          flexDirection: "column",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(th: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: th.palette.divider
          })}
        />
      </Box>

      {/* Close (×) */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(th: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(th.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(th.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      <Typography variant="h6" sx={{ fontWeight: 700, px: 2.5, pt: 0.5, pr: 6 }}>
        {title}
      </Typography>
      <StepDots step={step} isWizard={isWizard} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* ---- Step: amount ---- */}
        {step === "amount" && (
          <Box sx={{ px: 2.5, py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How much would you like to invest in this bot?
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {BUY_QUICK_ADDS.map((v) => (
                <Button
                  key={v}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={() => bumpAmount(v)}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                >
                  +{formatUSD(v, { maximumFractionDigits: 0 })}
                </Button>
              ))}
            </Stack>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 1 }}>
              <Typography
                sx={(th) => ({
                  fontSize: 40,
                  fontWeight: 700,
                  color:
                    amount > 0
                      ? th.palette.success.main
                      : th.palette.text.disabled
                })}
              >
                $
              </Typography>
              <Box
                component="input"
                inputMode="decimal"
                placeholder="0"
                value={formatNumericInput(amountStr)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value.replace(/,/g, "");
                  if (/^\d*\.?\d*$/.test(v)) setAmountStr(v);
                }}
                autoFocus
                sx={(th) => ({
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 40,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  color: th.palette.success.main,
                  "&::placeholder": { color: th.palette.text.disabled }
                })}
              />
            </Box>
            <Divider />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              Adding {t.name} to your account is free — you only commit the
              capital you invest above.
            </Typography>
          </Box>
        )}

        {/* ---- Step: settings (wizard bots only) ---- */}
        {step === "settings" && (
          <BotSettingsForm
            botName={t.name}
            allocatedAmount={amount}
            stocksList={t.assets}
          />
        )}

        {/* ---- Step: run ---- */}
        {step === "run" && (
          <Box sx={{ px: 2.5, py: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Assign account to run this bot on:
            </Typography>
            <TextField
              fullWidth
              size="small"
              select
              value={selectedAccountId}
              onChange={(e) => handleAccountChange(e.target.value)}
              sx={{ mt: 0.5 }}
              SelectProps={{
                displayEmpty: true,
                // The dropdown must render ABOVE the drawer (which sits
                // at modal+2) or it's occluded by the sheet — Sergey
                // 2026-05-21.
                MenuProps: SELECT_MENU_PROPS,
                renderValue: (v) =>
                  v ? (
                    selectedAccount?.label ?? "Select an account"
                  ) : (
                    <Typography component="span" color="text.secondary">
                      Select an account
                    </Typography>
                  )
              }}
            >
              {accountOptions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {a.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.sub}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value={ADD_BROKER}>+ Add broker account</MenuItem>
              <MenuItem value={NEW_PAPER}>New WhiteHat Paper account</MenuItem>
            </TextField>

            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary", display: "block", mt: 2 }}
            >
              Start:
            </Typography>
            <TextField
              fullWidth
              size="small"
              select
              value={startWhen}
              onChange={(e) => setStartWhen(e.target.value)}
              sx={{ mt: 0.5 }}
              SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
            >
              {TRADE_TIMING_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>

            {/* Selected account — show balances + explain the impact of
             *  running a bot on it. */}
            {selectedInfo && (
              <Box
                sx={(th) => ({
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: `1px solid ${th.palette.divider}`
                })}
              >
                <BalanceRow
                  label="Buying power"
                  value={formatUSD(selectedInfo.buyingPower)}
                />
                {!selectedInfo.isNew && (
                  <BalanceRow
                    label="Cash available to withdraw"
                    value={formatUSD(selectedInfo.cash)}
                  />
                )}
                <BalanceRow
                  label="This bot will allocate"
                  value={`−${formatUSD(amount)}`}
                  emphasize
                />
                {!selectedInfo.isNew && (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <BalanceRow
                      label="Buying power after"
                      value={formatUSD(selectedInfo.buyingPower - amount)}
                    />
                    <BalanceRow
                      label="Cash available after"
                      value={formatUSD(selectedInfo.cash - amount)}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Insufficient-funds check (live accounts only). */}
            {insufficientFunds && selectedInfo && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={(th) => ({
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(th.palette.error.main, 0.12)
                })}
              >
                <WarningAmberRounded
                  sx={{ fontSize: 18, color: "error.main", mt: 0.1 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedInfo.isNew
                    ? `${selectedInfo.name} isn't funded yet — ${formatUSD(
                        0
                      )} available. Add money to it before this bot can run, or pick another account.`
                    : `The amount you're investing (${formatUSD(
                        amount
                      )}) is more than this account's buying power (${formatUSD(
                        selectedInfo.buyingPower
                      )}). Lower the amount or pick another account.`}
                </Typography>
              </Stack>
            )}

            {/* Impact note — live vs. paper. */}
            {selectedInfo && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={(th) => ({
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(
                    selectedIsLive
                      ? th.palette.warning.main
                      : th.palette.info.main,
                    0.1
                  )
                })}
              >
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: selectedIsLive ? "warning.main" : "info.main",
                    mt: 0.1
                  }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedIsLive ? (
                    <>
                      This runs on a <strong>live account</strong> with real
                      money. The {formatUSD(amount)} you invest is moved into
                      the bot — your buying power and the cash available to
                      withdraw both drop by that amount until you sell or
                      liquidate the bot.
                    </>
                  ) : (
                    <>
                      This runs on a <strong>paper account</strong> —
                      simulated funds only. It mirrors real trading so you can
                      evaluate the bot, but it doesn't touch any real money,
                      buying power, or withdrawable cash.
                    </>
                  )}
                </Typography>
              </Stack>
            )}
          </Box>
        )}

        {/* ---- Step: connect broker ---- */}
        {step === "connect" && (
          <ConnectBrokerPanel
            initialView={connectView}
            onCreated={handleBrokerCreated}
            onCancel={() => setStep("run")}
          />
        )}

        {/* ---- Step: balance (after connecting a new live account) ---- */}
        {step === "balance" && selectedInfo && (
          <Box sx={{ px: 2.5, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {selectedInfo.name} connected
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Here's where the account stands before you run the bot.
            </Typography>

            <Box
              sx={(th) => ({
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${th.palette.divider}`
              })}
            >
              <BalanceRow
                label="Buying power"
                value={formatUSD(selectedInfo.buyingPower)}
              />
              <BalanceRow
                label="This bot needs"
                value={formatUSD(amount)}
                emphasize
              />
            </Box>

            {insufficientFunds && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={(th) => ({
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(th.palette.error.main, 0.12)
                })}
              >
                <WarningAmberRounded
                  sx={{ fontSize: 18, color: "error.main", mt: 0.1 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Not enough money.</strong> {selectedInfo.name} has{" "}
                  {formatUSD(selectedInfo.buyingPower)} available but this bot
                  needs {formatUSD(amount)}.
                </Typography>
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={(th) => ({
                mt: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: alpha(th.palette.info.main, 0.1)
              })}
            >
              <InfoOutlined
                sx={{ fontSize: 18, color: "info.main", mt: 0.1 }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Add funds to {selectedInfo.name} from your broker, then it'll
                be ready to run bots. You can also pick a different account on
                the next screen.
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer — per-step actions. The connect step carries its own
       *  Back/Create buttons inside the panel. */}
      {step !== "connect" && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, flexShrink: 0 }}>
            <Stack direction="row" spacing={1}>
              {step !== "amount" && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
                  onClick={() =>
                    setStep(
                      step === "settings"
                        ? "amount"
                        : step === "balance"
                          ? "connect"
                          : step === "run"
                            ? isWizard
                              ? "settings"
                              : "amount"
                            : "amount"
                    )
                  }
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                >
                  Back
                </Button>
              )}
              {step === "run" ? (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedAccountId || insufficientFunds}
                  startIcon={<RocketLaunchOutlined sx={{ fontSize: 18 }} />}
                  onClick={handleLaunch}
                  sx={{ flex: 1, fontWeight: 700, borderRadius: 2 }}
                >
                  Run bot
                </Button>
              ) : step === "balance" ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setStep("run")}
                  sx={{ flex: 1, fontWeight: 700, borderRadius: 2 }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={step === "amount" && amount <= 0}
                  onClick={() =>
                    setStep(
                      step === "amount"
                        ? isWizard
                          ? "settings"
                          : "run"
                        : "run"
                    )
                  }
                  sx={{ flex: 1, fontWeight: 700, borderRadius: 2 }}
                >
                  Next
                </Button>
              )}
            </Stack>
          </Box>
        </>
      )}
    </SwipeableDrawer>
  );
}

// ---- Internals ------------------------------------------------------------

/** Slim progress dots for the flow steps. */
function StepDots({ step, isWizard }: { step: Step; isWizard: boolean }) {
  const order: Step[] = isWizard
    ? ["amount", "settings", "run"]
    : ["amount", "run"];
  // The connect + balance sub-flows sit "on" the run step.
  const activeIdx = order.indexOf(
    step === "connect" || step === "balance" ? "run" : step
  );
  return (
    <Stack direction="row" spacing={0.75} sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
      {order.map((s, i) => (
        <Box
          key={s}
          sx={(th) => ({
            height: 4,
            flex: 1,
            borderRadius: 2,
            bgcolor:
              i <= activeIdx
                ? th.palette.primary.main
                : th.palette.action.selected
          })}
        />
      ))}
    </Stack>
  );
}

/** Label / value row for the run-step account balance panel. */
function BalanceRow({
  label,
  value,
  emphasize
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.4 }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontFeatureSettings: '"tnum" 1',
          color: emphasize ? "primary.main" : "text.primary"
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
